import { Badge, Button, Card, Icon, IconSet } from '@postscript/components';
import React from 'react';
import styled from 'styled-components';
import ConfirmModal from './ConfirmModal';
import { MessageCardData, MessageTemplate } from './MessageTemplatesModal';

const favoriteColor = 'var(--purple-8)';
const FavoriteButton = styled(Button)<{ isFavorite?: boolean }>`
  margin-top: var(--spacing-2);
  :hover,
  :active,
  :focus {
    color: ${favoriteColor};
    box-shadow: inset 0 0 0 var(--button-stroke) ${favoriteColor};
  }

  :not(:hover):focus {
    background: transparent;
  }

  ${({ isFavorite }) =>
    isFavorite
      ? `
    box-shadow: inset 0 0 0 var(--button-stroke) ${favoriteColor};
  `
      : `
    :not(:hover):focus {
      background: transparent;
      box-shadow: inset 0 0 0 var(--button-stroke) transparent;
    }
  `}
`;

const TemplateCard = styled(Card)<{ isActive: boolean }>`
  white-space: pre-wrap;
  border: 3px solid transparent;
  ${({ isActive }) =>
    isActive
      ? `
        border-color: var(--purple-core); 
        box-shadow: 0px 0px 5px 0px #5724E9;
      `
      : `
      :hover {
        border-color: var(--purple-core); 
        border-width: 1.5px;
        & > *:first-of-type {
          margin: 1.5px;
        }  
      }
    `}
`;

type TemplateFunction = (MessageTemplate: MessageCardData) => void;

interface Props {
  isActive: boolean;
  messageTemplate: MessageCardData;
  nextTemplate: MessageCardData;
  previousTemplate: MessageCardData;
  selectedTemplate: MessageTemplate | null;
  setSelectedTemplate: TemplateFunction;
  setFavoriteTemplate: TemplateFunction;
}

const handleRadioGroupKeyboardEvents = (
  event: React.KeyboardEvent<HTMLElement>,
  setSelectedTemplate: TemplateFunction,
  messageTemplate: MessageCardData,
  previousTemplate: MessageCardData,
  nextTemplate: MessageCardData,
) => {
  const target = event.target as HTMLElement;
  if (event.key === ' ') {
    setSelectedTemplate(messageTemplate);
  }

  if (
    (event.key === 'ArrowDown' || event.key === 'ArrowRight') &&
    target.nextSibling
  ) {
    setSelectedTemplate(nextTemplate);
    (target.nextSibling as HTMLElement).focus();
  }

  if (
    (event.key === 'ArrowLeft' || event.key === 'ArrowUp') &&
    target.previousSibling
  ) {
    setSelectedTemplate(previousTemplate);
    (target.previousSibling as HTMLElement).focus();
  }
};

const MessageTemplateCard = ({
  isActive,
  messageTemplate,
  nextTemplate,
  previousTemplate,
  selectedTemplate,
  setSelectedTemplate,
  setFavoriteTemplate,
}: Props): JSX.Element => {
  const [confirmFavoriteOpen, setConfirmFavoriteOpen] =
    React.useState<boolean>(false);
  const handleConfirmFavorite = () => {
    setConfirmFavoriteOpen(false);
    setFavoriteTemplate(messageTemplate);
  };
  const handleConfirmFavoriteCancel = () => setConfirmFavoriteOpen(false);
  const handleFavoriteClick = (evt: React.MouseEvent) => {
    evt.stopPropagation();
    setConfirmFavoriteOpen(true);
  };
  const isTemplateFavorited = messageTemplate.favorite;

  return (
    <>
      <TemplateCard
        isActive={isActive}
        onClick={() => setSelectedTemplate(messageTemplate)}
        role="radio"
        aria-checked={isActive}
        tabIndex={
          (selectedTemplate === null && previousTemplate == null) || isActive
            ? 0
            : -1
        }
        onKeyDown={(e) => {
          handleRadioGroupKeyboardEvents(
            e,
            setSelectedTemplate,
            messageTemplate,
            previousTemplate,
            nextTemplate,
          );
        }}
        data-testid="message-template"
      >
        <div>
          {messageTemplate.data.text}
          <div>
            <Badge style={{ backgroundColor: messageTemplate.color }}>
              {messageTemplate.category}
            </Badge>
          </div>
          <FavoriteButton
            isFavorite={isTemplateFavorited}
            icon={() => (
              <Icon
                component={IconSet.Heart}
                size={20}
                color={isTemplateFavorited ? `${favoriteColor}` : ''}
              />
            )}
            size="medium"
            variant="text"
            data-testid={
              isTemplateFavorited ? 'unfavorite-btn' : 'favorite-btn'
            }
            onClick={handleFavoriteClick}
          />
        </div>
      </TemplateCard>
      <ConfirmModal
        isOpen={confirmFavoriteOpen}
        onSubmit={handleConfirmFavorite}
        onCancel={handleConfirmFavoriteCancel}
        headerText={`Confirm Favorite${isTemplateFavorited ? ' Removal' : ''}`}
        bodyText={`This template will ${
          isTemplateFavorited
            ? 'no longer be classified as a favorite'
            : 'be classified as a favorite'
        }`}
      />
    </>
  );
};

export default MessageTemplateCard;
